import {useCallback, useState} from 'react';

/**
 * @typedef {Object} shownState
 * @property {boolean} shown - boolean that will change
 * @property {function} onClick - callback to change `shown` to `true`
 * @property {function} onClose - callback to change `shown` to `false`
 * @property {function} onToggle - callback to toggle `shown`
 */
type UseShown = (
    defaultState?: boolean
) => {
    shown: boolean,
    onClick: () => void,
    onClose: () => void,
    onToggle: () => void,
};

/**
 * Manages a simple boolean toggle state with stable show/hide/toggle callbacks.
 *
 * A convenience wrapper around `useState<boolean>` that exposes semantically
 * named callbacks for the three most common operations on a boolean flag —
 * showing (set to `true`), hiding (set to `false`), and toggling. Commonly
 * used to control the open/closed state of modals, dropdowns, tooltips, and
 * collapsible sections.
 *
 * All returned callbacks are memoized with `useCallback` and have stable
 * identities — safe to pass as props without causing unnecessary re-renders.
 *
 * @param defaultState - Initial value for the boolean. Defaults to `false`.
 * @returns An object with:
 *   - `shown` — current boolean state.
 *   - `onClick` — sets `shown` to `true`.
 *   - `onClose` — sets `shown` to `false`.
 *   - `onToggle` — flips `shown` between `true` and `false`.
 *
 * @example
 * ```tsx
 * function Dialog() {
 *   const {shown, onClick, onClose} = useShown();
 *   return (
 *     <>
 *       <button onClick={onClick}>Open</button>
 *       {shown && <Modal onClose={onClose} />}
 *     </>
 *   );
 * }
 * ```
 */
export const useShown: UseShown = (defaultState = false) => {
    const [shown, setShown] = useState<boolean>(defaultState);
    const onClick = useCallback(() => {
        setShown(true);
    }, []);
    const onClose = useCallback(() => {
        setShown(false);
    }, []);
    const onToggle = useCallback(() => {
        setShown(state => !state);
    }, []);
    return {
        shown,
        onClick,
        onClose,
        onToggle,
    };
};
